/**
 * React Query hooks for Fee Types API
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { feeTypesApi } from "@/api/feeTypes";
import { LIST_QUERY_OPTIONS } from "@/hooks/queryOptions";
import { turnedOffMessage } from "@/utils/feeTypeRetirement";
import type {
  ListFeeTypesParams,
  CreateFeeTypeRequest,
  UpdateFeeTypeRequest,
} from "@/types/feeType";

/**
 * Query keys for Fee Types
 */
export const feeTypesKeys = {
  all: ["feeTypes"] as const,
  lists: () => [...feeTypesKeys.all, "list"] as const,
  list: (params?: ListFeeTypesParams) =>
    [...feeTypesKeys.lists(), params] as const,
  details: () => [...feeTypesKeys.all, "detail"] as const,
  detail: (id: string) => [...feeTypesKeys.details(), id] as const,
  usage: (id: string) => [...feeTypesKeys.detail(id), "usage"] as const,
};

/**
 * Hook to list fee types with filters
 */
export const useFeeTypes = (
  params?: ListFeeTypesParams,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    ...LIST_QUERY_OPTIONS,
    queryKey: feeTypesKeys.list(params),
    queryFn: async () => {
      const response = await feeTypesApi.list(params);
      return response.data;
    },
    enabled: options?.enabled ?? true,
  });
};

/**
 * Hook to get fee type details by ID
 */
export const useFeeType = (id: string) => {
  return useQuery({
    queryKey: feeTypesKeys.detail(id),
    queryFn: async () => {
      const response = await feeTypesApi.get(id);
      return response.data;
    },
    enabled: !!id,
  });
};

/**
 * Hook: which classes carry a fee type, how many students, what it billed.
 * Keyed under the detail key, so an update to the fee type invalidates it.
 */
export const useFeeTypeUsage = (id: string) => {
  return useQuery({
    queryKey: feeTypesKeys.usage(id),
    queryFn: async () => {
      const response = await feeTypesApi.usage(id);
      return response.data;
    },
    enabled: !!id,
  });
};

/**
 * Hook to create fee type
 */
export const useCreateFeeType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateFeeTypeRequest) => {
      const response = await feeTypesApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeTypesKeys.lists() });
      toast.success("Fee type created successfully");
    },
    // Error handling done in form component via FormErrorAlert
  });
};

/**
 * Hook to update fee type
 */
export const useUpdateFeeType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateFeeTypeRequest;
    }) => {
      const response = await feeTypesApi.update(id, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: feeTypesKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: feeTypesKeys.detail(variables.id),
      });
      // Turning it off may also remove it from classes that no longer run:
      // say which, rather than doing it silently.
      if (variables.data.is_active === false && data?.retired_from_classes) {
        toast.success(turnedOffMessage("turned off", data.retired_from_classes));
      } else {
        toast.success("Fee type updated successfully");
      }
    },
    // Error handling done in form component via FormErrorAlert
  });
};

/**
 * Hook to delete fee type
 */
export const useDeleteFeeType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await feeTypesApi.delete(id);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: feeTypesKeys.lists() });
      toast.success(turnedOffMessage("archived", data?.retired_from_classes));
    },
    // Error handling done in form component via FormErrorAlert
  });
};
